class CartPromotionHandler {
  constructor() {
    this.threshold = Number(window.Theme?.freeGift?.threshold || 499) * 100; // In cents
    this.giftProductVariantId = window.Theme?.freeGift?.productVariantId;
    this.isProcessing = false;
    this.pendingUpdate = false;

    console.log('--- CartPromotionHandler (Real-time AJAX) ---');

    if (!this.giftProductVariantId) {
      console.error('CartPromotionHandler Error: No variant ID found.');
    }

    this.productTags = window.Theme?.freeGift?.productTags || {};

    // Escuchar el evento de actualización del carrito del tema
    const handleEvents = (event) => {
      console.log('--- DEBUG CartPromotionHandler Event Detected ---');
      console.log('Event type:', event.type);
      console.log('Event detail:', event.detail);

      // Evitar bucles infinitos si el evento viene de nuestras propias acciones AJAX
      if (event.detail?.source === 'cart-promotion-handler' || 
          event.detail?.data?.source === 'cart-promotion-handler') {
        console.log('Skipping event from self');
        return;
      }
      
      console.log('Triggering handleCartUpdate...');
      this.handleCartUpdate();
    };

    document.addEventListener('cart:update', handleEvents);
    document.addEventListener('cart:add', handleEvents);
    document.addEventListener('ajaxProduct:added', handleEvents);
    document.addEventListener('discount:update', handleEvents);

    // Verificación inicial
    console.log('Running initial check...');
    this.handleCartUpdate();
  }

  async handleCartUpdate() {
    if (!this.giftProductVariantId) return;
    if (this.isProcessing) {
      this.pendingUpdate = true;
      return;
    }

    // Usar un semáforo local para evitar ejecuciones concurrentes por navegación rápida
    this.isProcessing = true;

    try {
      console.log('Fetching cart from /cart.js...');
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, { cache: 'no-store' });
      if (!response.ok) throw new Error(`Cart request failed: ${response.status}`);
      const cart = await response.json();
      console.log('Cart fetched:', cart);
      
      // Filtrar el regalo por el ID del variante y por la propiedad especial
      const giftItems = cart.items.filter(item => 
        item.variant_id.toString() === this.giftProductVariantId.toString() ||
        (item.properties && item.properties._is_free_gift === 'true')
      );
      
      const totalGiftQuantity = giftItems.reduce((acc, item) => acc + item.quantity, 0);

      // 1. Calculate retailTotalPrice first
      let retailTotalPrice = 0;
      for (const item of cart.items) {
        const isThisAGift = giftItems.some(g => g.key === item.key);
        if (isThisAGift) continue;

        let tags = this.productTags[item.product_id];
        if (!tags) {
          const productResponse = await fetch(
            `${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(item.handle)}.js`
          );
          if (!productResponse.ok) throw new Error(`Product request failed: ${productResponse.status}`);
          const product = await productResponse.json();
          tags = product.tags;
          if (!Array.isArray(tags)) throw new Error('Product tags are missing');
          this.productTags[item.product_id] = tags;
        }

        if (tags.some(tag => tag.trim().toLowerCase() === 'retail')) {
          retailTotalPrice += item.line_price;
        }
      }

      console.log(`Current Status: RetailTotal=${retailTotalPrice/100}, GiftQty=${totalGiftQuantity}`);

      // 2. INTEGRITY CHECK: If exactly 1 gift and threshold met, STOP.
      if (totalGiftQuantity === 1 && retailTotalPrice >= this.threshold) {
        console.log('DECISION: Gift already present and correct. Stopping.');
        return;
      }

      // 3. CLEANUP: If duplicates or gift when not needed
      if (totalGiftQuantity > 1 || (totalGiftQuantity > 0 && retailTotalPrice < this.threshold)) {
        console.log('DECISION: Cleanup needed (Duplicates or Threshold not met)');
        for (const item of giftItems) {
           await this.removeGiftAjax(item.key, 0);
        }
        if (retailTotalPrice >= this.threshold) this.pendingUpdate = true;
        return;
      }
      
      // 4. ADD: If missing and threshold met
      if (retailTotalPrice >= this.threshold && totalGiftQuantity === 0) {
        console.log('DECISION: Add Gift');
        await this.addGiftAjax();
      } else {
        console.log('DECISION: No action needed');
      }
    } catch (e) {
      console.error('Error handling cart update:', e);
    } finally {
      this.isProcessing = false;
      if (this.pendingUpdate) {
        this.pendingUpdate = false;
        await this.handleCartUpdate();
      }
    }
  }

  async addGiftAjax() {
    try {
      const sections = this.#getCartSections();
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: this.giftProductVariantId,
            quantity: 1,
            properties: { '_is_free_gift': 'true' }
          }],
          sections: sections
        })
      });
      
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || `Cart add failed: ${response.status}`);
      this.#refreshCartComponents(result);
    } catch (e) {
      console.error('AJAX Error (add):', e);
      throw e;
    }
  }

  async removeGiftAjax(key, quantity = 0) {
    try {
      const sections = this.#getCartSections();
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
          sections: sections
        })
      });
      
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || `Cart change failed: ${response.status}`);
      this.#refreshCartComponents(result);
    } catch (e) {
      console.error('AJAX Error (remove):', e);
      throw e;
    }
  }

  #getCartSections() {
    const sections = [];
    document.querySelectorAll('cart-items-component').forEach(comp => {
      if (comp.dataset.sectionId) sections.push(comp.dataset.sectionId);
    });
    // Si no encuentra secciones en el DOM, intentamos con IDs comunes
    if (sections.length === 0) {
        const mainCart = document.querySelector('[data-section-id*="cart"]');
        if (mainCart) sections.push(mainCart.dataset.sectionId);
    }
    return sections;
  }

  #refreshCartComponents(cartData) {
    if (!cartData.sections) {
        console.warn('No sections returned from Shopify AJAX');
        window.location.reload();
        return;
    }

    // Crear un evento compatible con el del tema (CartUpdateEvent)
    const event = new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cartData,
        sourceId: 'cart-promotion-handler',
        data: {
          source: 'cart-promotion-handler',
          sections: cartData.sections,
          itemCount: cartData.item_count
        }
      }
    });

    // Notificar a todos los componentes del carrito para que se actualicen
    document.dispatchEvent(event);

    // Forzar actualización visual manual de las secciones recibidas
    const CartItemsComponents = document.querySelectorAll('cart-items-component');
    CartItemsComponents.forEach(component => {
        const sectionId = component.dataset.sectionId;
        if (cartData.sections[sectionId]) {
            // Si el componente tiene un método privado de manejo de actualización, intentamos simularlo
            // o simplemente dejamos que el evento haga su trabajo si el tema está bien suscrito.
            console.log(`Refreshing section: ${sectionId}`);
        }
    });

    console.log('AJAX UI Refresh triggered');
  }
}

// Inicialización
(function() {
    const start = () => {
        if (window.Theme?.freeGift?.productVariantId) {
            new CartPromotionHandler();
        } else {
            setTimeout(start, 500);
        }
    };
    start();
})();
